import { useEffect, useState } from 'react';
import { makePostCall } from '../../../utils/helper';

/**
 * The logged-in user's effective reporting LEVEL for one study coordinate
 * (center × modality), used to gate the Draft / Reviewed / Sign-off buttons.
 *
 * This is only a mirror of the server-side gate (submitReportOodocs / saveReport
 * both enforce the same level) — it exists to disable buttons the user can't use,
 * never as the sole authority.
 *
 * The backend resolves the level from the user's matrix rows with a role-default
 * fallback, so it always returns a sensible value even for unconfigured users.
 * `enforcement_enabled` tells the caller whether the backend will actually block:
 * when false, callers keep the pre-feature (role-based) gating so the UI and the
 * server stay consistent.
 */

export interface ReportingLevelInfo {
  level: string;              // code: NONE | VIEW | DRAFT | REVIEWED | SIGNOFF
  level_ordinal: number | null; // 0..4, null when unresolved
  owning_modality?: string | null;
  source?: string;
  enforcement_enabled: boolean;
  /** True when SignOff was withheld only because no signature is on file. */
  capped_for_signature?: boolean;
  /** Whether the user has a signature on file. Reported independently of the privilege
   *  flags — Sign Off is gated on it either way, since signing without one produces a
   *  report carrying no signature at all. */
  has_signature?: boolean;
}

/**
 * Returned instead of null when the lookup fails, so a non-null result always means
 * "settled". Callers gate the editor on that, and this shape degrades to the legacy
 * role-based behaviour (enforcement_enabled false, no ordinal) rather than blocking.
 */
const UNRESOLVED: ReportingLevelInfo = {
  level: 'UNKNOWN',
  level_ordinal: null,
  enforcement_enabled: false,
  // Fail OPEN on an unresolved lookup: a dead endpoint must not lock every user out of
  // sign-off. The backend remains the authority on what actually gets stamped.
  has_signature: true,
};

export function useReportingLevel(
  centerCode?: string | null,
  modality?: string | null
): ReportingLevelInfo | null {
  const [info, setInfo] = useState<ReportingLevelInfo | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!centerCode && !modality) {
      setInfo(null);
      return;
    }

    makePostCall('/my-reporting-level', {
      center_code: centerCode || null,
      modality: modality || null,
    })
      .then(res => {
        if (cancelled) return;
        const data = res.data?.data;
        if (!data) { setInfo(UNRESOLVED); return; }
        setInfo({
          ...data,
          // A backend that predates the flag omits it — treat that as "has one" so an
          // older server can't disable Sign Off for everybody.
          has_signature: data.has_signature !== false,
        });
      })
      .catch(() => {
        // Settle even on failure, so a dead endpoint can never leave the editor
        // waiting forever. UNRESOLVED falls back to legacy role-based gating.
        if (!cancelled) setInfo(UNRESOLVED);
      });

    return () => { cancelled = true; };
  }, [centerCode, modality]);

  return info;
}

export default useReportingLevel;
